import React, { useState, useEffect, useCallback } from 'react';
import { useAccount } from 'wagmi';
import { useWallet } from '@solana/wallet-adapter-react';
import { ButtonCard } from '@sw4p/ui';
import { ProjectConfig } from '../hooks/useProject';
import { useAuthToken } from '../hooks/useAuthToken';
import { getProjectWallet, getWalletBalances, withdrawFunds, getWithdrawFeeEstimate, WithdrawFeeEstimate } from '../services/api';
import { toast } from 'sonner';
import { logger } from '../utils/logger';

interface WalletBubbleProps {
    project: ProjectConfig;
}

interface TokenBalance {
    symbol: string;
    amount: string;
    usd_value: number;
}

interface WaaSWallet {
    waas_address: string;
    waas_wallet_id: string;
}

export default function WalletBubble({ project }: WalletBubbleProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [waasWallet, setWaasWallet] = useState<WaaSWallet | null>(null);
    const [balances, setBalances] = useState<TokenBalance[]>([]);
    const [withdrawAmount, setWithdrawAmount] = useState('');
    const [showWithdraw, setShowWithdraw] = useState(false);
    const [withdrawing, setWithdrawing] = useState(false);
    const [feeEstimate, setFeeEstimate] = useState<WithdrawFeeEstimate | null>(null);

    const { getAuthToken } = useAuthToken();

    const { isConnected: evmConnected, address: evmAddress } = useAccount();
    const { connected: solConnected, publicKey: solPublicKey } = useWallet();

    const isConnected = evmConnected || solConnected;
    const userAddress = evmAddress || solPublicKey?.toBase58();
    const chain = evmConnected ? 'EVM' : 'SOL';

    useEffect(() => {
        if (isOpen && isConnected && userAddress && !waasWallet) {
            fetchWaasWallet();
        }
    }, [isOpen, isConnected, userAddress]);

    useEffect(() => {
        if (waasWallet) {
            fetchBalances();
        }
    }, [waasWallet]);

    useEffect(() => {
        if (showWithdraw && withdrawAmount && parseFloat(withdrawAmount) > 0 && userAddress) {
            const debounce = setTimeout(() => {
                fetchFeeEstimate();
            }, 500);
            return () => clearTimeout(debounce);
        } else {
            setFeeEstimate(null);
        }
    }, [withdrawAmount, showWithdraw]);

    const fetchWaasWallet = async () => {
        if (!userAddress) return;
        setLoading(true);
        try {
            const authToken = await getAuthToken({ address: userAddress, chain });
            const wallet = await getProjectWallet(project.id, authToken);
            setWaasWallet({
                waas_address: wallet.waas_address,
                waas_wallet_id: wallet.waas_wallet_id,
            });
        } catch (error) {
            logger.error('[WalletBubble]', 'Failed to fetch WaaS wallet:', error);
            toast.error('Failed to create wallet');
        } finally {
            setLoading(false);
        }
    };

    const fetchBalances = async () => {
        if (!waasWallet || !userAddress) return;
        try {
            const authToken = await getAuthToken({ address: userAddress, chain });
            const data = await getWalletBalances(waasWallet.waas_address, authToken);
            setBalances(data.tokens || []);
        } catch (error) {
            logger.error('[WalletBubble]', 'Failed to fetch balances:', error);
        }
    };

    const fetchFeeEstimate = async () => {
        if (!userAddress || !withdrawAmount) return;
        try {
            const authToken = await getAuthToken({ address: userAddress, chain });
            const estimate = await getWithdrawFeeEstimate({
                amount: withdrawAmount,
                dest_chain: chain === 'EVM' ? 'ethereum' : 'solana',
                dest_address: userAddress,
            }, authToken);
            setFeeEstimate(estimate);
        } catch (error) {
            logger.error('[WalletBubble]', 'Failed to fetch fee estimate:', error);
        }
    };

    const handleWithdraw = async () => {
        if (!userAddress || !withdrawAmount) return;

        const amount = parseFloat(withdrawAmount);
        if (isNaN(amount) || amount <= 0) {
            toast.error('Please enter a valid amount');
            return;
        }

        setWithdrawing(true);
        try {
            const authToken = await getAuthToken({ address: userAddress, chain });
            const result = await withdrawFunds({
                amount: withdrawAmount,
                dest_chain: chain === 'EVM' ? 'ethereum' : 'solana',
                dest_address: userAddress,
            }, authToken);

            toast.success(`Withdrawal initiated! TX: ${result.tx_id.slice(0, 8)}...`);
            setShowWithdraw(false);
            setWithdrawAmount('');
            setFeeEstimate(null);
            setTimeout(fetchBalances, 3000);
        } catch (error) {
            logger.error('[WalletBubble]', 'Withdrawal failed:', error);
            toast.error(error instanceof Error ? error.message : 'Withdrawal failed');
        } finally {
            setWithdrawing(false);
        }
    };

    const getUsdcBalance = useCallback(() => {
        const usdc = balances.find(b => b.symbol === 'USDC');
        return usdc ? parseFloat(usdc.amount) : 0;
    }, [balances]);

    const handleMaxWithdraw = () => {
        setWithdrawAmount(String(getUsdcBalance()));
    };

    if (!isConnected) return null;

    const hasBalance = balances.some(b => parseFloat(b.amount) > 0);

    return (
        <>
            {/* System-tray chip (bottom-right corner) */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="xp-tray-bubble"
                title={`${project.token_symbol} Wallet`}
            >
                {hasBalance && <span className="xp-tray-bubble__dot" />}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                <span style={{ fontSize: 11 }}>{project.token_symbol} Wallet</span>
            </button>

            {/* Expanded tray panel */}
            {isOpen && (
                <div className="xp-tray-panel">
                    <div className="xp-tray-panel__header">
                        <span>Your {project.token_symbol} Wallet</span>
                        <button
                            onClick={() => setIsOpen(false)}
                            style={{
                                background: 'none', border: 'none', cursor: 'pointer',
                                color: '#fff', display: 'flex', alignItems: 'center', padding: 2,
                            }}
                        >
                            <svg width="12" height="12" viewBox="0 0 10 10" fill="none">
                                <path d="M1 1L9 9M9 1L1 9" stroke="white" strokeWidth="1.6" strokeLinecap="round" />
                            </svg>
                        </button>
                    </div>

                    <div className="xp-tray-panel__body">
                        {/* Source wallet indicator */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10, fontSize: 11, color: 'var(--xp-text-gray)' }}>
                            <span style={{
                                width: 7, height: 7, borderRadius: '50%',
                                background: evmConnected && solConnected ? '#3C8D0D' : '#E5A100',
                                display: 'inline-block', flexShrink: 0,
                            }} />
                            {evmConnected && solConnected ? (
                                <span>Source: <strong>{chain === 'EVM' ? 'EVM' : 'Solana'}</strong> (both connected)</span>
                            ) : evmConnected ? (
                                <span>Source: <strong>EVM</strong> ({evmAddress?.slice(0, 6)}...{evmAddress?.slice(-4)})</span>
                            ) : (
                                <span>Source: <strong>Solana</strong> ({solPublicKey?.toBase58().slice(0, 6)}...{solPublicKey?.toBase58().slice(-4)})</span>
                            )}
                        </div>

                        {loading ? (
                            <div style={{ display: 'flex', justifyContent: 'center', padding: '20px 0' }}>
                                <span className="xp-spinner" />
                            </div>
                        ) : waasWallet ? (
                            <>
                                {/* Wallet address */}
                                <div style={{
                                    background: '#EDF4FE',
                                    border: '1px solid var(--xp-border-card)',
                                    borderRadius: 2,
                                    padding: '6px 10px',
                                    marginBottom: 10,
                                }}>
                                    <div style={{ fontSize: 10, color: 'var(--xp-text-gray)', marginBottom: 2 }}>Wallet Address</div>
                                    <div style={{ fontSize: 11, color: 'var(--xp-text-ink)', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                        {waasWallet.waas_address}
                                    </div>
                                </div>

                                {/* Balances */}
                                <div style={{ marginBottom: 10 }}>
                                    {balances.length > 0 ? balances.map((token, i) => (
                                        <div key={i} style={{
                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                            padding: '6px 8px', marginBottom: 4,
                                            background: '#fff',
                                            border: '1px solid var(--xp-border-card)',
                                            borderRadius: 2,
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <div style={{
                                                    width: 26, height: 26,
                                                    background: '#D3E5FA', borderRadius: '50%',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    fontSize: 11, fontWeight: 700, color: 'var(--xp-titlebar-flat)',
                                                }}>
                                                    {token.symbol.charAt(0)}
                                                </div>
                                                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--xp-text-ink)' }}>{token.symbol}</span>
                                            </div>
                                            <div style={{ textAlign: 'right' }}>
                                                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--xp-text-ink)' }}>
                                                    {token.symbol === 'USDC' || token.symbol === 'USDT'
                                                        ? parseFloat(token.amount).toFixed(2)
                                                        : parseFloat(token.amount).toLocaleString()}
                                                </div>
                                                <div style={{ fontSize: 10, color: 'var(--xp-text-gray)' }}>${token.usd_value.toFixed(2)}</div>
                                            </div>
                                        </div>
                                    )) : (
                                        <p style={{ fontSize: 12, color: 'var(--xp-text-gray)', textAlign: 'center', padding: '12px 0' }}>No tokens yet</p>
                                    )}
                                </div>

                                <ButtonCard
                                    variant="default"
                                    onClick={() => setShowWithdraw(true)}
                                    disabled={getUsdcBalance() <= 0}
                                    style={{ width: '100%', justifyContent: 'center' } as React.CSSProperties}
                                >
                                    Withdraw
                                </ButtonCard>
                            </>
                        ) : (
                            <ButtonCard
                                variant="primary"
                                onClick={fetchWaasWallet}
                                style={{ width: '100%', justifyContent: 'center' } as React.CSSProperties}
                            >
                                Create Wallet
                            </ButtonCard>
                        )}
                    </div>
                </div>
            )}

            {/* Withdraw modal */}
            {showWithdraw && (
                <div style={{
                    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 16,
                }}>
                    <div style={{
                        background: '#FAFBFC',
                        border: '1px solid var(--xp-border-input)',
                        borderRadius: 6,
                        width: '100%', maxWidth: 340,
                        overflow: 'hidden',
                        boxShadow: 'var(--xp-shadow-window)',
                        fontFamily: 'var(--sw4p-font-body)',
                    }}>
                        {/* Modal title bar */}
                        <div style={{
                            background: 'var(--xp-titlebar-flat)', color: '#fff',
                            padding: '6px 12px', fontSize: 13, fontWeight: 600,
                            borderBottom: '2px solid var(--tenant-accent, var(--xp-titlebar-flat))',
                        }}>
                            Withdraw USDC
                        </div>
                        <div style={{ padding: 16 }}>
                            <div style={{ position: 'relative', marginBottom: 12 }}>
                                <input
                                    type="number"
                                    value={withdrawAmount}
                                    onChange={(e) => setWithdrawAmount(e.target.value)}
                                    placeholder="Amount"
                                    max={getUsdcBalance()}
                                    className="xp-input"
                                    style={{ paddingRight: 48 }}
                                />
                                <button
                                    onClick={handleMaxWithdraw}
                                    style={{
                                        position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                                        fontFamily: 'var(--sw4p-font-body)',
                                        fontSize: 11, fontWeight: 700,
                                        color: 'var(--tenant-accent, var(--xp-titlebar-flat))',
                                        background: 'none', border: 'none', cursor: 'pointer',
                                    }}
                                >
                                    MAX
                                </button>
                            </div>

                            {feeEstimate && (
                                <div style={{
                                    background: '#EDF4FE',
                                    border: '1px solid var(--xp-border-card)',
                                    borderRadius: 2, padding: '8px 10px', marginBottom: 10, fontSize: 12,
                                }}>
                                    {[
                                        ['Fee Policy', feeEstimate.fee_tier],
                                        ['Platform Fee', `$${feeEstimate.platform_fee.toFixed(2)}`],
                                        ['Total Fees', `$${feeEstimate.total_fees.toFixed(2)}`],
                                    ].map(([label, val]) => (
                                        <div key={label} style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--xp-text-gray)', marginBottom: 3 }}>
                                            <span>{label}</span><span>{val}</span>
                                        </div>
                                    ))}
                                    <div style={{
                                        display: 'flex', justifyContent: 'space-between',
                                        fontWeight: 700, color: 'var(--xp-text-ink)',
                                        borderTop: '1px solid var(--xp-border-card)', paddingTop: 4, marginTop: 4,
                                    }}>
                                        <span>You Receive</span>
                                        <span>${feeEstimate.net_receive.toFixed(2)}</span>
                                    </div>
                                </div>
                            )}

                            <div style={{ fontSize: 11, color: 'var(--xp-text-gray)', marginBottom: 12 }}>
                                Available: ${getUsdcBalance().toLocaleString()} USDC
                            </div>

                            <div style={{ display: 'flex', gap: 8 }}>
                                <ButtonCard
                                    variant="default"
                                    onClick={() => {
                                        setShowWithdraw(false);
                                        setWithdrawAmount('');
                                        setFeeEstimate(null);
                                    }}
                                    style={{ flex: 1, justifyContent: 'center' } as React.CSSProperties}
                                >
                                    Cancel
                                </ButtonCard>
                                <ButtonCard
                                    variant="primary"
                                    onClick={handleWithdraw}
                                    disabled={withdrawing || !withdrawAmount || parseFloat(withdrawAmount) <= 0}
                                    style={{ flex: 1, justifyContent: 'center' } as React.CSSProperties}
                                >
                                    {withdrawing ? (
                                        <span className="xp-spinner" style={{ width: 14, height: 14 }} />
                                    ) : (
                                        'Withdraw'
                                    )}
                                </ButtonCard>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
